import { api } from "./api";
import { useAuthStore } from "./auth";
import type { Paginated, PageSize } from "./pagination";

export type MessageState =
  | "RAW"
  | "CANONICAL"
  | "ENRICHED"
  | "PENDING_MAPPING"
  | "FAILED"
  | "COMPLETED";

export interface MessageSummary {
  message_id: string;
  source: string;
  raw_format: string | null;
  canonical_type: string | null;
  state: MessageState;
  correlation_id: string | null;
  received_at: string;
  normalized_at: string | null;
  enriched_at: string | null;
  last_error: string | null;
  payload_hash: string;
}

export interface TimelineEntry {
  occurred_at: string;
  event_type: string;
  outcome: "success" | "failure" | "denied";
  details: Record<string, unknown> | null;
}

export interface DeliverySummary {
  delivery_id: string;
  target_id: string;
  target_connector: string;
  state: string;
  attempts: number;
  delivered_at: string | null;
  last_error: string | null;
  target_payload: Record<string, unknown> | null;
  target_blob_ref: string | null;
  target_reference: string | null;
}

export interface MessageDetail extends MessageSummary {
  raw_blob_ref: string;
  canonical_payload: Record<string, unknown> | null;
  metadata: Record<string, unknown> | null;
  workflow_id: string | null;
  timeline: TimelineEntry[];
  deliveries: DeliverySummary[];
}

export interface MessageFilters {
  state?: MessageState;
  source?: string;
  canonical_type?: string;
  page?: number;
  page_size?: PageSize;
}

export const messagesApi = {
  list: (filters: MessageFilters = {}) =>
    api.get<Paginated<MessageSummary>>("/messages", {
      query: Object.fromEntries(Object.entries(filters).filter(([, v]) => v !== undefined && v !== "")),
    }),

  get: (id: string) => api.get<MessageDetail>(`/messages/${id}`),

  replay: (id: string) => api.post<MessageSummary>(`/messages/${id}/replay`),

  async fetchRaw(id: string): Promise<string> {
    const token = useAuthStore.getState().token;
    const r = await fetch(`/api/admin/messages/${id}/raw`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!r.ok) throw new Error(`Failed to load raw: ${r.statusText}`);
    return r.text();
  },
};
